import { initializeApp } from 'firebase/app';
import { initializeFirestore, connectFirestoreEmulator, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// ---------------------------------------------------------------------------
// Prod Firebase – always used for auth, functions, cUsers, profile
// ---------------------------------------------------------------------------
let app;
let prodDb;    // Prod Firestore – for cUsers, profile
let auth;
let functions;
let prodStorage;
let isInitialized = false;
let initializationError = null;

// ---------------------------------------------------------------------------
// Data Firebase – Prod db normally, Test db when user env = 'Test'
// Resolved once at module init from localStorage so every page that calls
// getFirebase() automatically gets the right db without any per-page logic.
// ---------------------------------------------------------------------------
let dataDb;
let dataStorage;

// ---------------------------------------------------------------------------
// Test Firebase refs – set during env=Test init, or lazily via getTestFirebaseForAdmin()
// ---------------------------------------------------------------------------
let _testApp;
let _testDb;
let _testStorage;
let _testInitialized = false;
let _testInitError = null;

const resolveUserEnv = () => {
  try { return localStorage.getItem('userEnv') || 'Prod'; } catch { return 'Prod'; }
};

// Initialise the Test Firebase app on demand (shared by env=Test boot and admin lazy-init).
const initTestApp = () => {
  if (_testInitialized) {
    if (_testInitError) throw _testInitError;
    return { db: _testDb, storage: _testStorage, app: _testApp };
  }
  const testRequiredVars = [
    'VITE_FIREBASE_TEST_API_KEY',
    'VITE_FIREBASE_TEST_AUTH_DOMAIN',
    'VITE_FIREBASE_TEST_PROJECT_ID',
    'VITE_FIREBASE_TEST_STORAGE_BUCKET',
    'VITE_FIREBASE_TEST_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_TEST_APP_ID',
  ];
  const missingVars = testRequiredVars.filter(v => !import.meta.env[v]);
  if (missingVars.length > 0) {
    _testInitError = new Error(`Missing test Firebase env vars: ${missingVars.join(', ')}`);
    _testInitialized = true;
    throw _testInitError;
  }
  try {
    const testConfig = {
      apiKey: import.meta.env.VITE_FIREBASE_TEST_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_TEST_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_TEST_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_TEST_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_TEST_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_TEST_APP_ID,
    };
    _testApp = initializeApp(testConfig, 'pcake-test');
    _testDb = initializeFirestore(_testApp, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
    _testStorage = getStorage(_testApp);
    _testInitialized = true;
    _testInitError = null;
  } catch (error) {
    console.error('Error initializing Test Firebase:', error);
    _testInitError = error;
    _testInitialized = true;
    throw error;
  }
  return { db: _testDb, storage: _testStorage, app: _testApp };
};

const initializeFirebase = () => {
  if (isInitialized) {
    if (initializationError) {
      throw new Error(`Firebase initialization failed: ${initializationError.message}`);
    }
    return { db: dataDb, auth, functions, storage: dataStorage };
  }

  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  const requiredEnvVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
  ];

  const missingEnvVars = requiredEnvVars.filter(
    (envVar) => !import.meta.env[envVar]
  );

  if (missingEnvVars.length > 0) {
    const error = new Error(
      `Missing required Firebase environment variables: ${missingEnvVars.join(', ')}`
    );
    console.error(error.message);
    initializationError = error;
    prodDb = null;
    dataDb = null;
    auth = { currentUser: null };
    functions = null;
    prodStorage = null;
    dataStorage = null;
  } else {
    try {
      app = initializeApp(firebaseConfig);
      prodDb = initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager()
        })
      });
      auth = getAuth(app);
      setPersistence(auth, browserLocalPersistence)
        .catch(error => console.warn('Could not set auth persistence:', error.message));
      functions = getFunctions(app, 'us-central1');
      prodStorage = getStorage(app);

      if (import.meta.env.VITE_FIREBASE_EMULATOR === 'true') {
        connectFirestoreEmulator(prodDb, 'localhost', 8080);
        connectAuthEmulator(auth, 'http://localhost:9099');
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }

      // Resolve data db: use Test Firebase if the user is in the Test environment.
      // localStorage is already set before the page reload that triggers this init.
      if (resolveUserEnv() === 'Test') {
        try {
          const testFirebase = initTestApp();
          dataDb = testFirebase.db;
          dataStorage = testFirebase.storage;
          console.log('Using Test Firebase for data collections.');
        } catch (testErr) {
          console.warn('Test Firebase init failed, falling back to Prod:', testErr.message);
          dataDb = prodDb;
          dataStorage = prodStorage;
        }
      } else {
        dataDb = prodDb;
        dataStorage = prodStorage;
      }

      isInitialized = true;
      initializationError = null;
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      initializationError = error;
      prodDb = null;
      dataDb = null;
      auth = { currentUser: null };
      functions = null;
      prodStorage = null;
      dataStorage = null;
    }
  }

  if (!dataDb || !prodDb || !auth || !functions || !dataStorage) {
    throw new Error(
      `Firebase initialization failed: ${initializationError?.message || 'Unknown error'}`
    );
  }

  return { db: dataDb, auth, functions, storage: dataStorage };
};

/**
 * Returns { db, auth, functions, storage } where db/storage are env-appropriate
 * (Test project for Test users, Prod project for Prod users).
 * All pages use this — no per-page env logic needed.
 */
export const getFirebase = () => {
  if (!isInitialized) {
    return initializeFirebase();
  }
  if (initializationError) {
    throw new Error(`Firebase initialization failed: ${initializationError.message}`);
  }
  if (!dataDb || !auth || !functions || !dataStorage) {
    throw new Error('Firebase services are not available. Initialization may have failed.');
  }
  return { db: dataDb, auth, functions, storage: dataStorage };
};

/**
 * Returns the Prod Firestore db.
 * Only needed in service-layer functions that must always target Prod
 * regardless of the user's env (cUsers and profile).
 */
export const getProdDb = () => {
  if (!isInitialized) initializeFirebase();
  return prodDb;
};

/**
 * Returns the Test Firebase { db, storage, app }.
 * Lazy-initialises on first call even for Prod-env users (e.g. admin HouseKeeping).
 * Throws if VITE_FIREBASE_TEST_* env vars are missing.
 */
export const getTestFirebaseForAdmin = () => {
  if (!_testInitialized) return initTestApp();
  if (_testInitError) throw _testInitError;
  return { db: _testDb, storage: _testStorage, app: _testApp };
};

// ---------------------------------------------------------------------------
// Env helpers – called from App.jsx to persist the user's env to localStorage
// so getFirebase() picks the right db on the next page load/reload.
// ---------------------------------------------------------------------------
export const setCurrentUserEnv = (env) => {
  try { localStorage.setItem('userEnv', env === 'Test' ? 'Test' : 'Prod'); } catch (_) {}
};

export const getCurrentUserEnv = () => resolveUserEnv();